import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { CareerLevelRatesRestDataProvider, CareerLevelRate } from '../../../data/CareerLevelRatesRestDataProvider';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { useTranslation } from 'react-i18next';

const CareerLevelRatesTab: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [rates, setRates] = useState<CareerLevelRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const provider = React.useMemo(() => new CareerLevelRatesRestDataProvider(), []);
  const columns: DetailedListColumn<CareerLevelRate>[] = React.useMemo(() => [
    { key: 'careerLevel', header: t('screens:careerLevels.columns.careerLevel'), dataField: 'careerLevel', type: 'string' },
    { key: 'costRate', header: t('screens:careerLevels.columns.costRate'), dataField: 'costRate', type: 'number', align: 'right', render: (r) => `€${r.costRate.toFixed(2)}` },
    { key: 'commercialRate', header: t('screens:careerLevels.columns.commercialRate'), dataField: 'commercialRate', type: 'number', align: 'right', render: (r) => `€${r.commercialRate.toFixed(2)}` },
  ], [t]);

  useEffect(() => {
    setLoading(true);
    provider.getAll()
      .then(setRates)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [provider]);

  const handleChange = async (newRates: CareerLevelRate[], action: 'add' | 'edit' | 'delete', changed: CareerLevelRate) => {
    try {
      if (action === 'add') {
        await provider.add(changed);
      } else if (action === 'edit') {
        await provider.update(changed.id, changed);
      } else if (action === 'delete') {
        await provider.delete(changed.id);
      }
      // Always refresh from API after mutation
      const refreshed = await provider.getAll();
      setRates(refreshed);
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="career-level-rates-tab">
      <SubsectionPanel title={t('screens:careerLevels.title')}>
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
         <InfoPanel header="About these rates">
          These are the rates which will be presented to the user when they select a career level for a project. You can add, edit, or remove rates as needed. The cost rate is the internal cost, while the commercial rate is what you charge clients.
        </InfoPanel>
        <DetailedListComponent
          dataSource={{ data: rates, setData: setRates }}
          columns={columns}
          rowKey={r => r.id}
          onChange={handleChange}
        />
        {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
      </SubsectionPanel>
    </div>
  );
};

export default CareerLevelRatesTab;
